import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { siteConfig, articles } from '@/mocks/siteData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.slug !== article.slug)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-400">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-950 mb-3">文章未找到</h1>
            <p className="text-foreground-600 mb-6">您访问的文章不存在或已被移除</p>
            <Link to="/" className="inline-block px-6 py-2.5 rounded-md bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
    image: 'https://readdy.ai/api/search-image?query=Warm%20minimalist%20creative%20writing%20workspace%20with%20notebook%20and%20warm%20lighting%2C%20editorial%20photography%20style%20with%20amber%20tones&width=1200&height=630&seq=article-og-01&orientation=landscape'
  };

  const renderContent = (content: string) => {
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];
    let inList = false;

    lines.forEach((line, i) => {
      if (line.startsWith('### ')) {
        if (inList) { inList = false; }
        elements.push(
          <h3 key={i} className="text-lg font-semibold text-foreground-950 mt-8 mb-3">
            {line.replace('### ', '')}
          </h3>,
        );
      } else if (line.startsWith('## ')) {
        if (inList) { inList = false; }
        elements.push(
          <h2 key={i} className="text-xl font-bold text-foreground-950 mt-10 mb-4 pb-2 border-b border-background-200/70">
            {line.replace('## ', '')}
          </h2>,
        );
      } else if (/^\d+\.\s/.test(line)) {
        if (!inList) { inList = true; }
        elements.push(
          <li key={i} className="ml-6 text-foreground-700 leading-relaxed mb-2 list-decimal">
            {line.replace(/^\d+\.\s/, '')}
          </li>,
        );
      } else if (line.trim() === '') {
        if (inList) { inList = false; }
      } else {
        if (inList) { inList = false; }
        elements.push(
          <p key={i} className="text-foreground-700 leading-relaxed mb-4">
            {line}
          </p>,
        );
      }
    });

    return elements;
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="px-4 md:px-6 max-w-6xl mx-auto py-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        {/* Article Header */}
        <article className="bg-background-50">
          <div className="px-4 md:px-6 max-w-3xl mx-auto py-10 md:py-14">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100/80 text-accent-600 font-medium">
                {article.categoryName}
              </span>
              <span className="text-xs text-foreground-500">{article.date}</span>
              <span className="text-xs text-foreground-500 flex items-center gap-1">
                <i className="ri-time-line"></i> {article.readTime} 分钟阅读
              </span>
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed">
              {article.excerpt}
            </p>
          </div>
        </article>

        {/* Article Content */}
        <div className="bg-background-50">
          <div className="px-4 md:px-6 max-w-3xl mx-auto pb-16">
            <div className="prose-custom">
              {renderContent(article.content)}
            </div>

            {/* Tags / Share */}
            <div className="mt-12 pt-8 border-t border-background-200/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">分享到：</span>
                <button className="w-9 h-9 flex items-center justify-center rounded-md bg-background-200/70 text-foreground-600 hover:text-primary-500 hover:bg-primary-100/70 transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-md bg-background-200/70 text-foreground-600 hover:text-primary-500 hover:bg-primary-100/70 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-md bg-background-200/70 text-foreground-600 hover:text-primary-500 hover:bg-primary-100/70 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link"></i>
                </button>
              </div>
              <Link
                to={`/category/${article.category}`}
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 font-medium"
              >
                <i className="ri-arrow-left-line"></i> 返回{article.categoryName}
              </Link>
            </div>
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-18 bg-background-100">
            <div className="px-4 md:px-6 max-w-6xl mx-auto">
              <h2 className="text-xl font-bold text-foreground-950 mb-8">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra, idx) => {
                  const imgUrls = [
                    'https://readdy.ai/api/search-image?query=A%20warm%20minimalist%20study%20desk%20with%20notebook%20and%20pen%20bathed%20in%20soft%20morning%20sunlight%2C%20potted%20plant%20nearby%2C%20clean%20aesthetic%2C%20peaceful%20creative%20atmosphere%2C%20editorial%20photography%20style%20with%20warm%20amber%20tones%20and%20gentle%20shadows&width=600&height=400&seq=article-01&orientation=landscape',
                    'https://readdy.ai/api/search-image?query=Colorful%20abstract%20paint%20splashes%20swirling%20in%20creative%20motion%20on%20white%20canvas%2C%20artistic%20explosion%20of%20warm%20coral%20and%20teal%20colors%2C%20inspiring%20creative%20energy%2C%20high%20detail%20editorial%20photography%20with%20soft%20lighting&width=600&height=400&seq=article-02&orientation=landscape',
                    'https://readdy.ai/api/search-image?query=Modern%20marketing%20strategy%20visualized%20through%20floating%20holographic%20data%20charts%20and%20content%20icons%20in%20warm%20amber%20and%20teal%2C%20clean%20minimalist%20aesthetic%2C%20futuristic%20yet%20warm%20atmosphere%2C%20editorial%20style%20with%20soft%20ambient%20light&width=600&height=400&seq=article-03&orientation=landscape',
                    'https://readdy.ai/api/search-image?query=Elegant%20academic%20library%20with%20wooden%20shelves%20filled%20with%20books%2C%20warm%20reading%20lamp%20glow%20on%20a%20polished%20desk%20with%20open%20notebook%2C%20scholarly%20atmosphere%2C%20refined%20minimalist%20aesthetic%2C%20amber%20and%20ivory%20tones%2C%20editorial%20photography&width=600&height=400&seq=article-04&orientation=landscape',
                    'https://readdy.ai/api/search-image?query=Professional%20business%20presentation%20scene%20with%20sleek%20laptop%20displaying%20charts%2C%20modern%20glass-walled%20conference%20room%2C%20warm%20ambient%20lighting%2C%20clean%20corporate%20aesthetic%2C%20amber%20and%20charcoal%20tones%2C%20editorial%20product%20photography&width=600&height=400&seq=article-05&orientation=landscape',
                    'https://readdy.ai/api/search-image?query=Vibrant%20social%20media%20content%20creation%20workspace%20with%20smartphone%20displaying%20colorful%20feed%2C%20creative%20desk%20setup%20with%20warm%20lighting%2C%20modern%20influencer%20aesthetic%2C%20coral%20and%20teal%20accents%2C%20editorial%20style%20with%20soft%20ambient%20glow&width=600&height=400&seq=article-06&orientation=landscape',
                    'https://readdy.ai/api/search-image?query=Clean%20modern%20desk%20with%20laptop%20showing%20productivity%20app%2C%20surrounded%20by%20minimalist%20stationery%20and%20warm%20desk%20lamp%2C%20organized%20creative%20workspace%2C%20amber%20and%20ivory%20tones%2C%20editorial%20product%20photography%20with%20soft%20natural%20lighting&width=600&height=400&seq=article-07&orientation=landscape',
                    'https://readdy.ai/api/search-image?query=Cozy%20reading%20nook%20with%20vintage%20typewriter%20and%20scattered%20manuscript%20pages%2C%20warm%20golden%20hour%20light%20streaming%20through%20window%2C%20nostalgic%20creative%20atmosphere%2C%20amber%20and%20sepia%20tones%2C%20artistic%20editorial%20photography%20with%20dreamy%20soft%20focus&width=600&height=400&seq=article-08&orientation=landscape',
                    'https://readdy.ai/api/search-image?query=Professional%20speaker%20at%20modern%20podium%20with%20warm%20stage%20lighting%2C%20engaged%20audience%20in%20soft%20focus%2C%20corporate%20event%20atmosphere%2C%20confident%20presentation%20style%2C%20amber%20and%20charcoal%20tones%2C%20editorial%20event%20photography&width=600&height=400&seq=article-09&orientation=landscape',
                  ];
                  const imgIndex = articles.findIndex((a) => a.slug === ra.slug);

                  return (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 overflow-hidden group cursor-pointer"
                    >
                      <div className="relative h-40 overflow-hidden">
                        <img
                          src={imgUrls[imgIndex % imgUrls.length]}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {ra.title}
                        </h3>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-xs text-foreground-500">{ra.date}</span>
                          <span className="text-xs text-foreground-500">{ra.readTime} 分钟阅读</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}